/**
 * `JasenedutRoute` — the `/jasenedut` member surface (029 US3 / FR-008).
 *
 * Renders the published `benefit` content as category-grouped sections to
 * proto parity (the proto's `Jasenedut.tsx`): a header, a
 * page-level demo disclosure (FR-009 — the listed benefits are demo examples,
 * not genuine partner offers), then one `<h2>` section per category (in the
 * proto order, unknown categories appended) with a `JasenedutBenefitCard` per
 * entry. This replaces the Phase A `JasenedutComingSoon` "Tulossa" stub (#59);
 * the URL, menu wiring, and member-role gating are preserved.
 *
 * Data through the port (P3 / FR-012): the route reads ONLY `useBenefits` (the
 * `ContentReaderPort` hook) — it never imports a concrete adapter (enforced by
 * the `no-content-adapter-import` architecture check, which scans
 * `content/routes/`).
 *
 * State matrix (FR-014):
 *   - `isPending`  -> a loading skeleton (`role="status"`).
 *   - `isError`    -> re-throw so the route's `PortErrorBoundary` (wired by the
 *                     `portBoundary: 'content'` route descriptor) renders
 *                     `ContentUnavailable` (mirrors `LakiruutuRoute` /
 *                     `ArticleListPage`).
 *   - `[]`         -> the defined empty state "Ei etuja saatavilla." (covers
 *                     both "no published benefits" AND, post-D6, "filter yields
 *                     zero" — R-US3-5; the grouping handles a [] input cleanly).
 *   - populated    -> the category-grouped list, ordered by `order` within each
 *                     group (the adapter sorts; the route preserves it).
 *
 * Filtering (research D6, D6 CONFIRMED: city filter IN, geolocation "Lähin"
 * DEFERRED — research.md D10): `<JasenedutFilters>` renders the category pills
 * ("Kaikki edut" show-all default + the proto categories) and, only while
 * "Hotellit ja kylpylät" is active, a native city `<select>`. The route owns
 * the filter STATE and the `visibleBenefits` pipeline: category pill -> (hotels
 * only) city -> `groupBenefitsByCategory`. The city options are DERIVED from the
 * hotels' `cities` (gate flag C: clean `cities.includes(city)` membership, never
 * the proto's hardcoded id-based special-casing). A category filter that yields
 * zero benefits renders the SAME empty state, never a boundary throw (R-US3-5).
 *
 * Spec references: `specs/029-cms-driven-member-surfaces/` spec FR-008 / FR-009
 * / FR-014, research D6 + D10, data-model "Read shape 3"; FR-049 (SanitizedHtml).
 */

import { type JSX, useState } from 'react';

import { AnalyticsRouteReady } from '../../analytics/AnalyticsCaptureProvider.js';
import { useTranslate } from '../../i18n/useTranslate.js';
import type { Benefit } from '../ContentReaderPort.js';
import { useBenefits } from '../hooks/useBenefits.js';
import { DetailSurfaceShell } from './detailSurfaceShell.js';
import { JasenedutBenefitCard } from './JasenedutBenefitCard.js';
import { type CategoryPill, JasenedutFilters } from './JasenedutFilters.js';

const headingClasses = 'm-0 text-2xl font-bold text-foreground';
const disclosureClasses =
  'm-0 rounded-md border border-border bg-muted px-4 py-3 text-sm text-muted-foreground';
const groupClasses = 'flex flex-col gap-4';
const groupHeadingClasses = 'm-0 text-lg font-semibold text-foreground';
const listClasses = 'flex flex-col gap-4';
const emptyStateClasses = 'm-0 py-8 text-center text-sm text-muted-foreground';
const skeletonWrapperClasses = 'flex flex-col gap-4';
const skeletonCardClasses = 'h-32 w-full animate-pulse rounded-lg bg-muted';

/**
 * Proto category grouping order (the proto's Jasenedut). The `<h2>`
 * sections render in this order; any category NOT in this list is appended
 * after, in first-seen order (never dropped). The strings ARE the authored
 * `benefit.category` values (the snapshot's free-string category), not i18n
 * keys — categories are content, rendered verbatim as the section label.
 */
const PROTO_CATEGORY_ORDER: readonly string[] = [
  'Hotellit ja kylpylät',
  'Liikunta ja hyvinvointi',
  'Koulutukset',
  'Vakuutukset',
  'Muut edut',
];

/**
 * The hotels category — the only one that surfaces the city filter (D6). Pinned
 * here (and matched against the authored `benefit.category`) so the city-filter
 * gate is a single named constant rather than a scattered string literal.
 */
const HOTELS_CATEGORY = 'Hotellit ja kylpylät';

/**
 * Sentinel value for the "Kaikki edut" show-all pill. A non-category string so
 * it can never collide with an authored `benefit.category`; `activeCategory ===
 * ALL_CATEGORY` means "no category filter".
 */
const ALL_CATEGORY = '__all__';

interface BenefitGroup {
  readonly category: string;
  readonly items: readonly Benefit[];
}

/**
 * Group benefits by their `category` field, ordering the groups by
 * {@link PROTO_CATEGORY_ORDER}; unknown categories are appended in first-seen
 * (insertion) order so a future authored category surfaces rather than
 * silently disappearing. Within a group the adapter's `order` sort is
 * preserved (the input array is already ordered).
 */
function groupBenefitsByCategory(benefits: readonly Benefit[]): readonly BenefitGroup[] {
  const byCategory = new Map<string, Benefit[]>();
  for (const benefit of benefits) {
    const existing = byCategory.get(benefit.category);
    if (existing !== undefined) {
      existing.push(benefit);
    } else {
      byCategory.set(benefit.category, [benefit]);
    }
  }

  const groups: BenefitGroup[] = [];
  for (const category of PROTO_CATEGORY_ORDER) {
    const items = byCategory.get(category);
    if (items !== undefined) {
      groups.push({ category, items });
      byCategory.delete(category);
    }
  }
  // Map iteration preserves insertion order; the known categories were deleted
  // above, so what remains are the unknowns in first-seen order.
  for (const [category, items] of byCategory) {
    groups.push({ category, items });
  }
  return groups;
}

export function JasenedutRoute(): JSX.Element {
  const t = useTranslate();
  const query = useBenefits();
  const [activeCategory, setActiveCategory] = useState<string>(ALL_CATEGORY);
  const [activeCity, setActiveCity] = useState<string>('');

  if (query.isError) {
    // Re-throw into the route's PortErrorBoundary (portBoundary: 'content').
    // The `?? new Error(...)` guard mirrors LakiruutuRoute / ArticleListPage:
    // React Query's type union permits `isError` with a `null` error.
    throw (
      query.error ?? new Error('useBenefits resolved to an error state without an Error instance')
    );
  }

  const benefits = query.isPending ? [] : query.data;

  // D6 filter pipeline (now live): category pill -> (hotels only) city ->
  // `groupBenefitsByCategory`. `visibleBenefits` is the single point both
  // filters compose through.
  const categoryFiltered =
    activeCategory === ALL_CATEGORY
      ? benefits
      : benefits.filter((benefit) => benefit.category === activeCategory);

  // City options DERIVE from ALL hotels in the read set (gate flag C: clean
  // `cities` membership, never a hardcoded city list), independent of the
  // current city pick so the option list stays stable as the user switches.
  const hotelCities = [
    ...new Set(
      benefits
        .filter((benefit) => benefit.category === HOTELS_CATEGORY)
        .flatMap((benefit) => benefit.cities),
    ),
  ].sort((left, right) => left.localeCompare(right, 'fi'));

  // The city filter is specific to the hotels category and only meaningful when
  // a hotel actually carries a city.
  const showCityFilter = activeCategory === HOTELS_CATEGORY && hotelCities.length > 0;

  const visibleBenefits =
    showCityFilter && activeCity !== ''
      ? categoryFiltered.filter((benefit) => benefit.cities.includes(activeCity))
      : categoryFiltered;

  const groups = groupBenefitsByCategory(visibleBenefits);

  const pills: readonly CategoryPill[] = [
    { value: ALL_CATEGORY, label: t('jasenedut.allCategories') },
    ...PROTO_CATEGORY_ORDER.map((category) => ({ value: category, label: category })),
  ];

  return (
    <DetailSurfaceShell as="section" ariaLabelledBy="jasenedut-title" fallbackTo="/">
      <div className="flex flex-col gap-6">
        <h1 id="jasenedut-title" className={headingClasses}>
          {t('jasenedut.heading')}
        </h1>
        <p className={disclosureClasses}>{t('jasenedut.demoDisclosure')}</p>
        {query.isPending ? (
          <div
            role="status"
            aria-busy="true"
            aria-label={t('common.loading')}
            data-testid="jasenedut-skeleton"
            className={skeletonWrapperClasses}
          >
            <span aria-hidden="true" className={skeletonCardClasses} />
            <span aria-hidden="true" className={skeletonCardClasses} />
          </div>
        ) : benefits.length === 0 ? (
          // No benefits published at all: the empty state, no filter controls.
          <>
            <AnalyticsRouteReady pageKey={{ kind: 'memberRoute', route: 'member-benefits' }} />
            <p className={emptyStateClasses}>{t('jasenedut.empty')}</p>
          </>
        ) : (
          <>
            <AnalyticsRouteReady pageKey={{ kind: 'memberRoute', route: 'member-benefits' }} />
            <JasenedutFilters
              pills={pills}
              activeCategory={activeCategory}
              onSelectCategory={(value) => {
                setActiveCategory(value);
                // Reset the city pick so a stale city never filters a fresh
                // category (and so re-entering hotels starts from "all cities").
                setActiveCity('');
              }}
              cityOptions={showCityFilter ? hotelCities : null}
              activeCity={activeCity}
              onSelectCity={(city) => {
                setActiveCity(city);
              }}
              t={t}
            />
            {groups.length === 0 ? (
              // A category filter narrowed to zero benefits: SAME empty copy
              // (R-US3-5), the route stays live (no boundary throw).
              <p className={emptyStateClasses}>{t('jasenedut.empty')}</p>
            ) : (
              groups.map((group) => (
                <section key={group.category} className={groupClasses}>
                  <h2 className={groupHeadingClasses}>{group.category}</h2>
                  <div className={listClasses}>
                    {group.items.map((benefit) => (
                      <JasenedutBenefitCard key={benefit.id} benefit={benefit} t={t} />
                    ))}
                  </div>
                </section>
              ))
            )}
          </>
        )}
      </div>
    </DetailSurfaceShell>
  );
}
